import { useState, useRef } from "react";

interface InspectionModalProps {
  open: boolean;
  onClose: () => void;
}

export default function InspectionModal({ open, onClose }: InspectionModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name) {
        data.append(input.name, input.value);
      }
    });
    try {
      await fetch("https://readdy.ai/api/form/d7f59cuivmjfhtdrfov0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-[#0a0e1a] border border-[#1a3a5c]/40 rounded-lg w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div>
            <h3 className="text-white font-extrabold text-lg">Free Engineering Inspection</h3>
            <p className="text-gray-400 text-xs mt-0.5">No obligation. No guesswork. Just technical clarity.</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1a3a5c]/20 mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-[#4a7fa5] text-3xl"></i>
              </div>
              <h4 className="text-white font-bold text-xl mb-2">Request Received!</h4>
              <p className="text-gray-400 text-sm">
                Our engineering team will contact you within 24 hours to schedule your free inspection.
              </p>
              <button
                onClick={onClose}
                className="mt-6 bg-[#1a3a5c] text-white font-bold px-8 py-3 rounded-md whitespace-nowrap"
              >
                Close
              </button>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex flex-col gap-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 text-xs font-semibold mb-1.5 block">Full Name *</label>
                  <input
                    name="full_name"
                    type="text"
                    required
                    placeholder="John Smith"
                    className="w-full bg-[#0f1525] border border-white/10 text-white text-sm px-4 py-2.5 rounded-md focus:outline-none focus:border-[#4a7fa5] placeholder-gray-600"
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-xs font-semibold mb-1.5 block">Phone *</label>
                  <input
                    name="phone"
                    type="tel"
                    required
                    placeholder="(561) 000-0000"
                    className="w-full bg-[#0f1525] border border-white/10 text-white text-sm px-4 py-2.5 rounded-md focus:outline-none focus:border-[#4a7fa5] placeholder-gray-600"
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-300 text-xs font-semibold mb-1.5 block">Email *</label>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="you@company.com"
                  className="w-full bg-[#0f1525] border border-white/10 text-white text-sm px-4 py-2.5 rounded-md focus:outline-none focus:border-[#4a7fa5] placeholder-gray-600"
                />
              </div>

              <div>
                <label className="text-gray-300 text-xs font-semibold mb-1.5 block">Property Type *</label>
                <select
                  name="property_type"
                  required
                  className="w-full bg-[#0f1525] border border-white/10 text-white text-sm px-4 py-2.5 rounded-md focus:outline-none focus:border-[#4a7fa5]"
                >
                  <option value="">Select property type</option>
                  <option value="Condominium">Condominium</option>
                  <option value="Commercial Building">Commercial Building</option>
                  <option value="Hotel">Hotel</option>
                  <option value="Parking Garage">Parking Garage</option>
                  <option value="HOA Community">HOA Community</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="text-gray-300 text-xs font-semibold mb-1.5 block">Property Address</label>
                <input
                  name="property_address"
                  type="text"
                  placeholder="123 Main St, Miami, FL"
                  className="w-full bg-[#0f1525] border border-white/10 text-white text-sm px-4 py-2.5 rounded-md focus:outline-none focus:border-[#4a7fa5] placeholder-gray-600"
                />
              </div>

              <div>
                <label className="text-gray-300 text-xs font-semibold mb-1.5 block">
                  Describe the leak issue (max 500 chars)
                </label>
                <textarea
                  name="leak_description"
                  rows={3}
                  maxLength={500}
                  placeholder="Where is the leak? How long has it been happening? What repairs have been attempted?"
                  className="w-full bg-[#0f1525] border border-white/10 text-white text-sm px-4 py-2.5 rounded-md focus:outline-none focus:border-[#4a7fa5] placeholder-gray-600 resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="bg-[#1a3a5c] hover:bg-[#0f2a45] disabled:opacity-60 text-white font-bold px-6 py-3.5 rounded-md transition-colors whitespace-nowrap text-sm"
              >
                {submitting ? "Sending..." : "Request My Free Inspection"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
